import { useState, useEffect, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { rgpdClient } from '../lib/rgpd';
import { logger } from '../lib/logger';

interface CookiePreferences {
  analytics: boolean;
}

interface CookieConsent {
  accepted: boolean;
  preferences: CookiePreferences;
  lastUpdated: string;
}

const COOKIE_CONSENT_KEY = 'cookie_consent';

export const useCookieConsent = () => {
  const [consent, setConsent] = useState<CookieConsent | null>(null);
  const [showBanner, setShowBanner] = useState<boolean | null>(null);

  // Charger la configuration RGPD
  const { data: rgpdConfig } = useQuery({
    queryKey: ['rgpd-config'],
    queryFn: () => rgpdClient.getConfig(),
    staleTime: 5 * 60 * 1000,
    cacheTime: 30 * 60 * 1000,
  });

  // Initialiser l'état du consentement une seule fois au montage
  useEffect(() => {
    if (showBanner === null) { // Seulement lors de l'initialisation
      try {
        const storedConsent = localStorage.getItem(COOKIE_CONSENT_KEY);
        if (storedConsent) {
          const parsedConsent = JSON.parse(storedConsent);
          setConsent(parsedConsent);
          setShowBanner(false);
          
          logger.debug('Loaded stored cookie consent', {
            category: 'RGPD',
            data: { consent: parsedConsent }
          });
        } else {
          setShowBanner(true);
          
          logger.debug('No stored cookie consent found', {
            category: 'RGPD'
          });
        }
      } catch (error) {
        logger.error('Error loading cookie consent', {
          category: 'RGPD',
          data: { error }
        });
        setShowBanner(true);
      }
    }
  }, []); // Exécuté une seule fois au montage

  // Sauvegarder le consentement
  const saveConsent = useCallback((newConsent: CookieConsent) => {
    try {
      const consentToSave = {
        ...newConsent,
        lastUpdated: new Date().toISOString()
      };

      localStorage.setItem(COOKIE_CONSENT_KEY, JSON.stringify(consentToSave));
      setConsent(consentToSave);
      setShowBanner(false);

      logger.debug('Cookie consent saved', {
        category: 'RGPD',
        data: { consent: consentToSave }
      });
    } catch (error) {
      logger.error('Error saving cookie consent', {
        category: 'RGPD',
        data: { error }
      });
    }
  }, []);

  // Accepter tous les cookies
  const acceptAll = useCallback(() => {
    saveConsent({
      accepted: true,
      preferences: { analytics: true },
      lastUpdated: new Date().toISOString()
    });
  }, [saveConsent]);

  // Refuser tous les cookies
  const rejectAll = useCallback(() => {
    saveConsent({
      accepted: false,
      preferences: { analytics: false },
      lastUpdated: new Date().toISOString()
    });
  }, [saveConsent]);

  // Sauvegarder les préférences
  const savePreferences = useCallback((preferences: CookiePreferences) => {
    saveConsent({
      accepted: true,
      preferences,
      lastUpdated: new Date().toISOString()
    });
  }, [saveConsent]);

  // Réinitialiser le consentement
  const resetConsent = useCallback(() => {
    localStorage.removeItem(COOKIE_CONSENT_KEY);
    setConsent(null);
    setShowBanner(true);

    logger.debug('Cookie consent reset', {
      category: 'RGPD'
    });
  }, []);

  return {
    consent,
    showBanner: showBanner === true, // Convertir null en false
    acceptAll,
    rejectAll,
    savePreferences,
    resetConsent,
    rgpdConfig
  };
};